/**
 * v2 (26/10/2026) — each target country's review and what its tab says: ready, how many rows to
 * look at, why it can't be pasted, or that it is still being read. Paste waits for every target.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { GroupCopyRequest, GroupCopyReview } from '@trafficpulse/shared';
import type { UseQueryResult } from '@tanstack/react-query';
import { useRef } from 'react';

import type { GroupCopyClip } from '@/features/drop-generator/components/group-copy/clipboard/group-copy-clipboard';

import { needTotal } from '../paste-summary';
import { useGroupCopyReviews } from '../use-group-copy-api';
import type { PasteTarget } from './use-paste-targets';

export interface TargetReview {
  target: PasteTarget;
  request: GroupCopyRequest | null;
  query: UseQueryResult<GroupCopyReview>;
  /** The review on screen: this request's answer, else the last one while the new one loads. */
  review: GroupCopyReview | undefined;
  /** The review on screen answers an older request — Paste waits for the new answer. */
  stale: boolean;
}

export type TargetTone = 'ready' | 'needs' | 'blocked' | 'reading';

export function useTargetReviews(clip: GroupCopyClip, targets: PasteTarget[]): TargetReview[] {
  const requests = targets.map((t) =>
    t.groupKey ? { source: clip.source, target: { countryKey: t.countryKey, groupKey: t.groupKey }, choices: t.choices } : null,
  );
  const queries = useGroupCopyReviews(requests);
  // useQueries drops the previous answer when a request changes: keep each country's last one on screen.
  const last = useRef(new Map<string, GroupCopyReview>());
  return targets.map((target, i) => {
    const query = queries[i] as UseQueryResult<GroupCopyReview>;
    if (query.data && !query.isPlaceholderData) last.current.set(target.id, query.data);
    const fresh = query.data !== undefined && !query.isPlaceholderData;
    const review = fresh ? query.data : (last.current.get(target.id) ?? query.data);
    return { target, request: requests[i] ?? null, query, review, stale: !fresh || query.isFetching };
  });
}

/** What the target's tab says. */
export function targetStatus(t: TargetReview): { tone: TargetTone; label: string } {
  if (!t.target.groupKey) return { tone: 'needs', label: 'pick a group' };
  const review = t.review;
  if (t.query.isError && !review) return { tone: 'blocked', label: 'could not read' };
  if (!review) return { tone: 'reading', label: 'reading…' };
  if (review.blockers.length > 0) return { tone: 'blocked', label: "can't paste" };
  const needs = needTotal(review, t.target.choices);
  return needs > 0 ? { tone: 'needs', label: `${needs} to look at` } : { tone: 'ready', label: 'ready' };
}

/** Why Paste can't run for every target yet; null = it can. */
export function pasteOffReason(all: TargetReview[], nameOf: (key: string) => string): string | null {
  for (const t of all) {
    const name = nameOf(t.target.countryKey);
    if (!t.target.groupKey) return `Pick the campaign group in ${name}.`;
    if (!t.review) return `Reading what comes over into ${name}…`;
    if (t.review.blockers.length > 0) return all.length > 1 ? `${name} can't be pasted yet — see its tab` : `Can't paste yet — see what to fix above`;
    if (t.stale) return 'Reading your last change…';
  }
  return null;
}
